import { Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { MovementRow } from '@/types'
import { cn, formatDateTime } from '@/utils/cn'
import { Link } from 'react-router-dom'

function isInTransitFlag(value?: string | null): boolean {
  return value === 'X' || value === 'x'
}

export function MovementTable({
  rows,
  loading,
  pagination,
  onPageChange,
  framed = false,
}: {
  rows: MovementRow[]
  loading?: boolean
  pagination?: { page: number; pageSize: number; total: number }
  onPageChange?: (page: number, pageSize: number) => void
  /** When true, wraps table in leap-table-wrap. Prefer false inside SectionCard variant=table. */
  framed?: boolean
}) {
  const showRegion = rows.some((r) => r.region)
  const columns: ColumnsType<MovementRow> = [
    ...(showRegion
      ? [
          {
            title: 'Region',
            dataIndex: 'region' as const,
            width: 80,
            render: (v: string | undefined) => (v ? <Tag>{v}</Tag> : '—'),
          },
        ]
      : []),
    {
      title: 'When',
      dataIndex: 'created_at',
      width: 170,
      render: (v) => formatDateTime(v),
    },
    {
      title: 'Tag',
      dataIndex: 'rfid_code',
      ellipsis: true,
      render: (v: string) => (
        <Link className="leap-link" to={`/tag-journey?code=${encodeURIComponent(v)}`}>
          {v}
        </Link>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'transfer_type',
      width: 120,
      render: (v) => (v ? <Tag color="red">{v}</Tag> : '—'),
    },
    {
      title: 'In Transit',
      dataIndex: 'in_transit',
      width: 100,
      render: (v?: string | null) =>
        isInTransitFlag(v) ? <Tag color="orange">X</Tag> : '—',
    },
    {
      title: 'Document',
      dataIndex: 'document',
      ellipsis: true,
      render: (v: string, row) => {
        const inTransit = isInTransitFlag(row.in_transit)
        const doc = inTransit ? '' : String(v ?? '').trim()
        if (!doc) return '—'
        return (
          <Link className="leap-link" to={`/movement-document?document=${encodeURIComponent(doc)}`}>
            {doc}
          </Link>
        )
      },
    },
    {
      title: 'Item',
      dataIndex: 'item_no',
      width: 80,
      render: (v) => v || '—',
    },
    {
      title: 'Sender',
      key: 'sender',
      ellipsis: true,
      render: (_, r) =>
        r.sender_code ? (
          <Link
            className="leap-link"
            to={`/party-journey?role=sender&code=${encodeURIComponent(r.sender_code)}`}
          >
            {r.sender_name || r.sender_code}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      title: 'Receiver',
      key: 'receiver',
      ellipsis: true,
      render: (_, r) =>
        r.receiver_code ? (
          <Link
            className="leap-link"
            to={`/party-journey?role=receiver&code=${encodeURIComponent(r.receiver_code)}`}
          >
            {r.receiver_name || r.receiver_code}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      title: 'Plant',
      dataIndex: 'warehouse',
      width: 110,
      render: (v) => v || '—',
    },
    {
      title: 'Site',
      dataIndex: 'customer_location',
      width: 120,
      render: (v) => v || '—',
    },
    {
      title: 'Material',
      key: 'material',
      ellipsis: true,
      render: (_, r) => r.material_desc || r.material_code || '—',
    },
  ]

  return (
    <div className={cn(framed && 'leap-table-wrap')}>
      <Table
        className="leap-table"
        rowKey={(r) => `${r.region ?? 'x'}-${r.id}`}
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="middle"
        scroll={{ x: 1300 }}
        pagination={
          pagination
            ? {
                current: pagination.page,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                onChange: onPageChange,
              }
            : false
        }
      />
    </div>
  )
}
